<template>
  <div :class="cx('relative flex items-center gap-[12px] rounded-r-none rounded-l-none !bg-[--color-brand-forrest-green-05] pl-[12px]', $attrs.class)">
    <Icon name="search" :width="24" :height="24" />
    <InputText
      ref="inputText"
      v-model="localKeyword"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="!isMobile && isOpen"
      :aria-controls="autocompleteId"
      :aria-activedescendant="!isMobile && activeIndex >= 0 ? `${autocompleteId}-option-${activeIndex}` : undefined"
      :placeholder="t('layout.search.placeholder')"
      :pt="{
        root: {
          class: ['focus:z-0 z-10 min-w-0 w-full rounded-r-none rounded-l-none !bg-[--color-brand-forrest-green-05] border-0 !shadow-none !pl-0 !placeholder:text-[#666666]'],
        }
      }"
      :pt-options="{ mergeProps: true }"
      :readonly="isMobile"
      @blur="handleDesktopBlur"
      @click="handleClick"
      @focus="desktopFocused = true"
      @keydown="handleAutocompleteKeydown($event, () => handleSubmit(), selectSuggestion)"
    />
    <KeywordAutocompleteList
      :id="autocompleteId"
      :active-index="activeIndex"
      :open="!isMobile && isOpen"
      :query="localKeyword"
      :suggestions="suggestions"
      @activate="activeIndex = $event"
      @select="selectSuggestion"
    />
  </div>

  <Dialog
    v-if="isMobile"
    v-model:visible="visible"
    :closable="false"
    :modal="true"
    :show-header="false"
    class="min-w-80 max-w-[90vw]"
    content-class="p-0 rounded-t-md"
  >
    <div class="flex flex-col gap-[16px] px-4 py-5">
      <Text
        as="h6"
        class="heading-mobile-h6 w-auto text-black"
      >
        Search Keywords
      </Text>
      <TopbarMarketSelector
        v-model="selectedMarketplace"
        :options="marketplaces"
        class="ingroup-action-field"
        :no-style="true"
        :is-nav="false"
        @on-marketplace-select="showMarketplaces"
      />
      <TopbarCountrySelector
        v-model="selectedCountry"
        :countries="countries"
        class="ingroup-action-field"
        :no-style="true"
        :is-nav="false"
        @on-show-countries="showCountries"
      />
      <div class="relative">
        <Textarea
          ref="mobileInput"
          v-model="localKeyword"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="isOpen"
          :aria-controls="mobileAutocompleteId"
          :aria-activedescendant="activeIndex >= 0 ? `${mobileAutocompleteId}-option-${activeIndex}` : undefined"
          :placeholder="t('layout.search.placeholder')"
          auto-resize
          :pt="{ root: { class: ['w-full min-h-[120px]'] } }"
          @blur="handleMobileBlur"
          @focus="mobileFocused = true"
          @keydown="handleAutocompleteKeydown($event, () => handleSubmit(), selectSuggestion)"
        />
        <KeywordAutocompleteList
          :id="mobileAutocompleteId"
          :active-index="activeIndex"
          :open="isOpen"
          :query="localKeyword"
          :suggestions="suggestions"
          @activate="activeIndex = $event"
          @select="selectSuggestion"
        />
      </div>
      <div class="v3-h6-bold-700-line-height-1.2 text-on-light flex items-center gap-1">
        {{ `Quota ${quotas?.keyword_tool.current_usage}/${quotas?.keyword_tool.feature_quota}` }}
        <div v-tooltip.bottom="'To keep the service fast and low-cost for all users, limits have been placed on some tools. All quotas are reset at midnight EST.'">
          <Icon
            name="info-circle"
            color="#FF8B00"
            :width="14"
            :height="14"
          />
        </div>
      </div>
      <div class="flex items-center gap-4">
        <Button
          class="w-full"
          label="Cancel"
          severity="secondary"
          outlined
          @click="closeMobileDialog"
        />
        <Button
          :disabled="localKeyword.length === 0"
          class="w-full"
          label="Search"
          @click="handleSubmit()"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang='ts'>
import { storeToRefs } from 'pinia';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import { useDialog } from 'primevue/usedialog';
import {
  type ComponentPublicInstance,
  computed,
  nextTick,
  ref,
  useId,
  useTemplateRef,
  watch,
  watchEffect,
} from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from '@/common/components/atoms/Icon';
import Text from '@/common/components/atoms/Text';
import { useMobile } from '@/common/composables/application';
import { useSubscriptionStore } from '@/common/stores/subscription';
import { cx } from '@/helpers/cva';
import { useSearchParams } from '@/modules/keywords/composables/router';
import TopbarCountrySelector from '@/modules/layout/components/atoms/TopbarCountrySelector';
import TopbarMarketSelector from '@/modules/layout/components/atoms/TopbarMarketSelector';
import KeywordAutocompleteList from '@/modules/layout/components/molecules/KeywordAutocompleteList';
import CountriesSelection from '@/modules/layout/components/organisms/CountriesSelection';
import MarketplacesSelection from '@/modules/layout/components/organisms/MarketplacesSelection';
import { useKeywordAutocomplete } from '@/modules/layout/composables/keywordAutocomplete';
import { useTopbarStore } from '@/modules/layout/stores/topbar';

const model = defineModel<string>();
const emit = defineEmits<{
  'onEnter': [keyword: string];
}>();

const { quotas } = storeToRefs(useSubscriptionStore());

const { isMobile } = useMobile();
const { searchParams } = useSearchParams();

const topbarStore = useTopbarStore();
const { selectedCountry, selectedMarketplace, marketplaces, countries } = storeToRefs(topbarStore);
const dialog = useDialog();

const { t } = useI18n();
const mobileInputRef = useTemplateRef<ComponentPublicInstance>('mobileInput');

const visible = ref(false);
const localKeyword = ref<string>('');
const desktopFocused = ref(false);
const mobileFocused = ref(false);
const autocompleteId = `keyword-autocomplete-${useId()}`;
const mobileAutocompleteId = `${autocompleteId}-mobile`;
const marketplaceCode = computed(() => selectedMarketplace.value?.code ?? '');
const countryCode = computed(() => selectedCountry.value ?? '');
const autocompleteEnabled = computed(() => desktopFocused.value || (visible.value && mobileFocused.value));
const {
  activeIndex,
  close,
  handleKeydown: handleAutocompleteKeydown,
  isOpen,
  suggestions,
} = useKeywordAutocomplete(localKeyword, marketplaceCode, countryCode, autocompleteEnabled);

const showMarketplaces = () => {
  dialog.open(MarketplacesSelection, {
    props: {
      modal: true,
      closable: false,
      showHeader: false,
      contentClass: 'p-0 rounded-t-md',
    },
    data: {
      marketplaces: marketplaces.value,
      selectedMarketplace: selectedMarketplace.value,
    },
    onClose: ({ data }) => {
      if (data?.selectedMarketplace) {
        selectedMarketplace.value = data.selectedMarketplace;
      }
    },
  });
};

const showCountries = () => {
  dialog.open(CountriesSelection, {
    props: {
      modal: true,
      closable: false,
      showHeader: false,
      contentClass: 'p-0 rounded-t-md',
    },
    data: {
      countries: countries.value,
      selectedCountry: selectedCountry.value,
    },
    onClose: ({ data }) => {
      if (data?.selectedCountry) {
        selectedCountry.value = data.selectedCountry;
      }
    },
  });
};

watch(localKeyword, (newKeyword) => {
  model.value = newKeyword.trim();
});

watchEffect(() => {
  const keyword = searchParams.value.keyword;
  localKeyword.value = keyword ? keyword : '';
});

function handleClick() {
  if (isMobile.value && !visible.value) {
    visible.value = true;

    nextTick(() => {
      mobileInputRef.value?.$el?.focus();
    });
  }
}

function handleDesktopBlur(): void {
  desktopFocused.value = false;
  close();
}

function handleMobileBlur(): void {
  mobileFocused.value = false;
  close();
}

function handleSubmit(keyword = localKeyword.value) {
  emit('onEnter', keyword.trim());
  close();
  desktopFocused.value = false;
  mobileFocused.value = false;
  visible.value = false;
}

function selectSuggestion(suggestion: string): void {
  localKeyword.value = suggestion;
  model.value = suggestion;
  handleSubmit(suggestion);
}

function closeMobileDialog(): void {
  close();
  mobileFocused.value = false;
  visible.value = false;
}

defineExpose({});
</script>
